import type { TagSortOrder } from '@mnd-frontend/graphql-schema';
import IconPin from '@mnd-frontend/icons/components/Pin';
import IconPinFilled from '@mnd-frontend/icons/components/PinFilled';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  DataTable,
  Dropdown,
  Pagination,
} from '@mnd-frontend/ui/publish/components';
import { SPACING_0_5, SPACING_1_5, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { TagManagementTagFragment } from '../../generated/types';
import useScrollTo from '../../hooks/useScrollTo';
import TagDataTableHeaders from './TagDataTableHeaders';

const NameRow = styled.div`
  display: flex;
  gap: ${SPACING_1_5};
  align-items: center;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING_1_5};

  strong {
    font-weight: bold !important;
  }
`;

const TableWrapper = styled.div`
  margin-top: ${SPACING_2};
`;

const Actions = styled.div`
  display: flex;
  gap: ${SPACING_0_5};
  justify-content: flex-end;
`;

const StyledPagination = styled(Pagination)`
  margin-top: ${SPACING_2};
`;

const HighlightButton = ({
  isHighlighted,
  onClick,
}: {
  isHighlighted: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    configuration="toned"
    onClick={onClick}
    aria-checked={isHighlighted}
    aria-label="Highlight"
    icon={isHighlighted ? <IconPinFilled /> : <IconPin />}
  />
);

type TagDataTableProps = {
  tags: TagManagementTagFragment[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  selectedIds: string[];
  searchQuery: string;
  pressroomId: string;
  selectedSort: TagSortOrder;
  onChangePage: (page: number) => void;
  onHighlightClick: (tag: TagManagementTagFragment) => void;
  onEditTagClick: (tag: TagManagementTagFragment) => void;
  onSelectImage: (tag: TagManagementTagFragment) => void;
  onMoveHighlightedTag: (tag: TagManagementTagFragment, above: boolean) => void;
  onSelectedIdsChanged: (ids: string[]) => void;
  onCombineClick: () => void;
  onDeleteClick: () => void;
  onSearchQueryChanged: (query: string) => void;
  onSelectedSortChanged: (sort: TagSortOrder) => void;
};

const TagDataTable = ({
  tags,
  totalCount,
  totalPages,
  currentPage,
  selectedIds,
  searchQuery,
  pressroomId,
  selectedSort,
  onChangePage,
  onHighlightClick,
  onEditTagClick,
  onSelectImage,
  onMoveHighlightedTag,
  onSelectedIdsChanged,
  onCombineClick,
  onDeleteClick,
  onSearchQueryChanged,
  onSelectedSortChanged,
}: TagDataTableProps) => {
  const [t] = useTranslation();
  const [Anchor, scrollTo] = useScrollTo();

  const rows = useMemo(() => {
    return tags.map((tag: any) => {
      return [
        [
          <NameRow>
            <Checkbox
              id={`checkbox:${tag.id}`}
              checked={selectedIds.includes(tag.id)}
              onChange={e =>
                e.target.checked
                  ? onSelectedIdsChanged([...selectedIds, tag.id])
                  : onSelectedIdsChanged(selectedIds.filter(x => x !== tag.id))
              }
            />
            <NameCell>
              {tag.image ? (
                <Avatar
                  type="contact"
                  image={tag.image.url ?? undefined}
                  name={tag.image.altText ?? ''}
                />
              ) : undefined}
              <strong>
                {tag.name} ({tag.materialCount})
              </strong>
            </NameCell>
          </NameRow>,
          tag.name,
        ],
        tag.materialCount,
        <Actions>
          <HighlightButton isHighlighted={tag.highlighted} onClick={() => onHighlightClick(tag)} />
          <Dropdown
            key="category-management-action-dropdown"
            title={t('category_management_actions_title')}
            hideTitle
            options={[
              {
                text: t('category_management_action_edit'),
                action: () => onEditTagClick(tag),
              },
              ...(tag.highlighted
                ? [
                    {
                      text: t('category_management_action_select_image'),
                      action: () => onSelectImage(tag),
                    },
                    {
                      text: t('common_move_up'),
                      action: () => onMoveHighlightedTag(tag, true),
                    },
                    {
                      text: t('common_move_down'),
                      action: () => onMoveHighlightedTag(tag, false),
                    },
                  ]
                : []),
            ]}
          />
        </Actions>,
      ];
    });
  }, [
    tags,
    selectedIds,
    t,
    onSelectedIdsChanged,
    onHighlightClick,
    onEditTagClick,
    onSelectImage,
    onMoveHighlightedTag,
  ]);

  return (
    <>
      <Box
        headerContent={
          <TagDataTableHeaders
            totalCount={totalCount}
            selectedIds={selectedIds}
            searchQuery={searchQuery}
            pressroomId={pressroomId}
            selectedSort={selectedSort}
            onCombineClick={onCombineClick}
            onDeleteClick={onDeleteClick}
            onSelectedIdsChanged={onSelectedIdsChanged}
            onSearchQueryChanged={onSearchQueryChanged}
            onSelectedSortChanged={onSelectedSortChanged}
          />
        }
        bodyPadding={false}
      >
        <Anchor />
        <TableWrapper>
          <DataTable
            hideHeaderOnMobile
            preserveLastColumnOnMobile
            headings={[
              {
                text: t('tags_table_header_name'),
              },
              {
                text: t('tags_table_header_material'),
              },
              { text: '', sortable: false },
            ]}
            rows={rows}
            totalRows={10}
            emptyMessage={
              searchQuery
                ? {
                    title: t('category_management_no-results_title'),
                    description: t('tags_table_empty_description'),
                  }
                : { title: t('category_management_empty_title'), description: '' }
            }
          />
        </TableWrapper>
      </Box>
      {totalPages > 1 && (
        <StyledPagination
          totalPages={totalPages}
          currentPage={currentPage}
          onChangePage={p => {
            scrollTo();
            onChangePage(p);
          }}
        />
      )}
    </>
  );
};

export default TagDataTable;
